import axios from "axios";
import { ChatCitation, PolicyChatMessage, PolicyChatSuggestion, PolicyChatThread, PolicyReference, SectionAnchor } from "../../db/schema";

// Officer-facing API for the agency's policies (server: routes/policiesRouter.ts).
const base = "/api/policies";

export type PolicyTag = { id: string; name: string; color: string | null; sections: number };

export type PolicyDocumentItem = {
  id: string;
  title: string;
  docType: "policy" | "statute" | "other";
  fileName: string | null;
  pageCount: number | null;
  sectionCount: number | null;
  textPages: boolean;
  updatedAt: string | null;
  createdAt: string | null;
  tags: PolicyTag[];
};

export type PolicySection = {
  id: string;
  sectionIndex: number;
  sectionNumber: string | null;
  heading: string | null;
  sectionPath: string | null;
  pageStart: number | null;
  pageEnd: number | null;
  charCount: number;
  tags: { id: string; name: string; color: string | null }[];
  // One normalized box per page the section spans; null for Word/txt documents.
  anchor: SectionAnchor | null;
};

export type PolicyDocumentDetail = {
  document: PolicyDocumentItem;
  sections: PolicySection[];
  url: string;
};

export type PolicyChatMessageView = Pick<PolicyChatMessage, "id" | "role" | "content" | "citations" | "created_at">;

export const policiesApi = {
  // The sidebar gate: true once the agency has a completed document.
  available: async (): Promise<boolean> => {
    const { data } = await axios.get<{ available: boolean }>(`${base}/available`);
    return Boolean(data.available);
  },
  list: () => axios.get<{ agencyName: string; documents: PolicyDocumentItem[] }>(base).then((r) => r.data),
  // Starter questions generated from the agency's documents; null = use defaults.
  suggestions: () => axios.get<{ questions: PolicyChatSuggestion[] | null }>(`${base}/suggestions`).then((r) => r.data.questions),
  document: (id: string) => axios.get<PolicyDocumentDetail>(`${base}/${id}`).then((r) => r.data),
  documentUrl: (id: string, download = false) => axios.get<{ url: string }>(`${base}/${id}/url`, { params: download ? { download: 1 } : {} }).then((r) => r.data.url),
  pages: (id: string) => axios.get<{ pages: { page: number | null; text: string }[] }>(`${base}/${id}/pages`).then((r) => r.data.pages),
  section: (id: string, sectionId: string) => axios.get<{ section: { content: string; heading: string | null; section_number: string | null }; documentTitle: string }>(`${base}/${id}/sections/${sectionId}`).then((r) => r.data),

  threads: () => axios.get<{ threads: PolicyChatThread[] }>(`${base}/chat/threads`).then((r) => r.data.threads),
  createThread: () => axios.post<{ thread: PolicyChatThread }>(`${base}/chat/threads`, {}).then((r) => r.data.thread),
  messages: (threadId: string) => axios.get<{ thread: PolicyChatThread; messages: PolicyChatMessageView[] }>(`${base}/chat/threads/${threadId}/messages`).then((r) => r.data),
  archiveThread: (threadId: string) => axios.delete(`${base}/chat/threads/${threadId}`),
  chatUrl: (threadId: string) => `${base}/chat/threads/${threadId}/messages`,
};

export type { ChatCitation };

export const POLICY_DOC_TYPE_LABELS: Record<PolicyDocumentItem["docType"], string> = { policy: "Policy", statute: "Statute", other: "Other" };

// Cases written before references were stored (or whose references were
// rebuilt) still carry the "<document title> § <section>" label in a policy
// suggestion. Look it up in the agency's documents instead: the longest title
// the label starts with, then the section by number (or heading).
export async function resolvePolicyLabel(label: string): Promise<PolicyReference | null> {
  const { documents } = await policiesApi.list();
  const doc = documents
    .filter((d) => label === d.title || label.startsWith(`${d.title} `))
    .sort((a, b) => b.title.length - a.title.length)[0];
  if (!doc) return null;
  const rest = label.slice(doc.title.length).trim();
  const number = rest.startsWith("§") ? rest.slice(1).trim() : null;
  const { sections } = await policiesApi.document(doc.id);
  const section = sections.find((s) => (number ? s.sectionNumber === number : s.heading === rest)) || null;
  if (!section) return null;
  return {
    documentId: doc.id,
    sectionId: section.id,
    documentTitle: doc.title,
    docType: doc.docType,
    sectionNumber: section.sectionNumber,
    heading: section.heading,
    pageStart: section.pageStart,
    pageEnd: section.pageEnd,
    excerpt: "",
    how: ["check"],
  };
}

// Where a section opens in the in-app viewer. Used by the editor's Policies
// panel and by chat citations.
export function policyDocumentPath(documentId: string, opts: { page?: number | null; sectionId?: string | null } = {}): string {
  const params = new URLSearchParams();
  if (opts.page) params.set("page", String(opts.page));
  if (opts.sectionId) params.set("section", opts.sectionId);
  const qs = params.toString();
  return `/policies/documents/${documentId}${qs ? `?${qs}` : ""}`;
}
